import {
  Directive,
  ElementRef,
  OnDestroy,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Platform } from '../core/platform.service';

/**
 * Directive to format US phone numbers in a user-friendly way while storing 10 digits only.
 *
 * User sees: (212) 690-0330
 * FormControl stores: 2126900330
 *
 * Features:
 * - Shows digits only during typing (no formatting)
 * - Formats to (XXX) XXX-XXXX on blur if 10 digits entered
 * - Stores exactly 10 digits in FormControl (per Coinflow API requirements)
 * - Handles editing by removing formatting on focus
 * - SSR-safe with Platform service checks
 * - Implements ControlValueAccessor to properly bridge between DOM and FormControl
 *
 * Usage:
 * <input matInput type="tel" appUsPhoneInput [formControl]="phoneControl" />
 */
@Directive({
  selector: 'input[appUsPhoneInput][matInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: UsPhoneInputDirective,
      multi: true,
    },
  ],
  host: {
    '(input)': 'onInput($event)',
    '(blur)': 'onBlur($event)',
    '(focus)': 'onFocus($event)',
  },
})
export class UsPhoneInputDirective implements ControlValueAccessor, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLInputElement>);
  private readonly platform = inject(Platform);

  // ControlValueAccessor callbacks
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private isDisabled = false;

  /**
   * Called by Angular when the FormControl value changes.
   * This is where we format 10-digit values for display.
   */
  writeValue(value: string | null): void {
    // SSR safety check
    if (!this.platform.isBrowser) {
      return;
    }

    const input = this.elementRef.nativeElement;

    // If value is exactly 10 digits, format for display
    if (value && typeof value === 'string' && /^\d{10}$/.test(value)) {
      input.value = this.formatForDisplay(value);
    } else if (value) {
      // If value exists but isn't 10 digits, display as-is
      input.value = value;
    } else {
      // Clear the input if no value
      input.value = '';
    }
  }

  /**
   * Register callback to notify FormControl of value changes from the view.
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Register callback to notify FormControl when the input is touched.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Handle disabled state changes from FormControl.
   */
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.platform.isBrowser) {
      this.elementRef.nativeElement.disabled = isDisabled;
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Handle user input - show digits only during typing.
   */
  onInput(event: Event): void {
    if (!this.platform.isBrowser || this.isDisabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Strip all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Remove leading "1" if present (in case user types it)
    const cleanDigits = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

    // Limit to 10 digits maximum
    const limitedDigits = cleanDigits.slice(0, 10);

    // Display digits only (no formatting during typing)
    input.value = limitedDigits;

    // Notify FormControl of the raw digits (10 digits or fewer)
    this.onChange(limitedDigits);
  }

  /**
   * Handle blur - format for display and store 10 digits in FormControl.
   */
  onBlur(event: Event): void {
    if (!this.platform.isBrowser || this.isDisabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Strip all non-digit characters
    let digits = value.replace(/\D/g, '');

    // Handle case where leading "1" got into the input
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    // If exactly 10 digits, format for display and store in FormControl
    if (digits.length === 10) {
      // Format for display: (XXX) XXX-XXXX
      input.value = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;

      // Notify FormControl of 10 digits only (per Coinflow API requirements)
      this.onChange(digits);
    } else if (digits.length > 0) {
      // Partial input - notify FormControl of raw digits for validation
      this.onChange(digits);
    } else {
      // Empty input - notify FormControl of empty string
      this.onChange('');
    }

    // Mark as touched for validation
    this.onTouched();
  }

  /**
   * Handle focus - remove formatting to show just digits for editing.
   */
  onFocus(event: Event): void {
    if (!this.platform.isBrowser || this.isDisabled) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Strip all formatting to show just digits
    const digits = value.replace(/\D/g, '');

    // Remove leading "1" if present (in case user typed it)
    const cleanDigits = digits.startsWith('1') && digits.length === 11 ? digits.slice(1) : digits;

    if (cleanDigits.length === 10) {
      input.value = cleanDigits;
    }
  }

  /**
   * Convert 10 digits to display format.
   * 2126900330 → (212) 690-0330
   */
  private formatForDisplay(digits: string): string {
    if (!digits || !/^\d{10}$/.test(digits)) {
      return digits; // Return as-is if not exactly 10 digits
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
}
